/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import { EditableComponent, EditableComponentProps } from '@adobe/aem-react-editable-components';
import { useSession } from '@marriott/mi-headless-utils';
import { useMounted } from '../../hooks/useMounted';
import { shouldUseMockData } from '../../utils/CommonUtils';
import { isUserAuthenticated, checkCampaignStatus } from '../../utils/OfferUtils';
import { OffersMBEHeroProps } from './OffersMBEHero.types';
import { StyledOffersMBEHero } from './OffersMBEHero.styles';
import { CountDownTimer } from './CountDownTimer';
import { MBEMessage } from './MBEMessage';
import { getMBEWrapperClass } from './utils/helper';
import { constants } from '../../constants/CommonConstants';

type editableCompOffersMBEHeroPropsWith = EditableComponentProps & OffersMBEHeroProps;

export const OffersMBEHeroConfig = {
  emptyLabel: 'OffersMBEHero',
  isEmpty: () => true,
  resourceType: `mi-aem-offers-spa/components/mbehero`,
};

export const OffersMBEHero: FC<editableCompOffersMBEHeroPropsWith> = props => {
  const { isAuthorMode, mbeDynamicWorkflow } = props;
  const [mockProps, setMockProps] = useState<any>({});

  const { mbeData, model, isPreview, mbeModelData } = !isAuthorMode ? props : mockProps;
  const offersData = mbeData?.responseObject?.offers?.mbeOfferById?.offers;

  const campaignStartDate = (mbeDynamicWorkflow ? offersData?.onCycleStartDate : mbeModelData?.campaignStartDate) || '';
  const campaignEndDate = (mbeDynamicWorkflow ? offersData?.onCycleEndDate : mbeModelData?.campaignEndDate) || '';

  const pathParam = useSearchParams();
  const cycleState = pathParam?.get('cycle');
  const isMounted = useMounted();

  const isMPOVar =
    offersData && Object.keys(offersData).length > 0 ? false : mbeData && Object.keys(mbeData).length > 0;

  const { CAMPAIGN_ACTIVE } = constants;
  const { datalayer } = useSession();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(isUserAuthenticated(datalayer));

  const [variation, setVariation] = useState(() =>
    isMPOVar ? CAMPAIGN_ACTIVE : checkCampaignStatus(campaignStartDate, campaignEndDate, cycleState, isPreview)
  );

  // Mock data loading with cleanup
  useEffect(() => {
    if (!isAuthorMode || !shouldUseMockData(isAuthorMode)) return;

    let isMounted = true;

    import('./__mock__/OffersMBEHero.mock').then(mockData => {
      if (isMounted) {
        setMockProps(mockData.mockProps);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isAuthorMode]);

  // Update variation only when dates change (no timer needed here)
  useEffect(() => {
    if (!campaignStartDate || !campaignEndDate || isAuthorMode) return;

    const newStatus = checkCampaignStatus(campaignStartDate, campaignEndDate, cycleState, isPreview);
    if (newStatus !== variation) {
      setVariation(newStatus);
    }
  }, [campaignStartDate, campaignEndDate, cycleState, isPreview, isAuthorMode]);

  useEffect(() => {
    setIsUserLoggedIn(isUserAuthenticated(datalayer));
  }, [datalayer]);

  // Memoize callback to prevent CountDownTimer re-renders
  const handleCountdownComplete = useCallback(() => {
    setVariation('active');
  }, []);

  return (
    <EditableComponent config={OffersMBEHeroConfig} {...props}>
      {isMounted && (
        <StyledOffersMBEHero data-testid="offers-OffersMBEHero" data-component-name="o-offers-OffersMBEHero">
          <div className={clsx(variation === 'inactive' && 'container', 'mbeHero-wrapper')}>
            <div className={clsx(getMBEWrapperClass(isUserLoggedIn, variation))}>
              <div className={clsx('content-wrapper mbe-layout')}>
                <MBEMessage {...{ isMPOVar, variation, isUserLoggedIn, ...props }} />

                {variation === 'inactive' && model?.countdownTimer && (
                  <>
                    <div className="vertical-divider" />
                    <div className="timer-align">
                      <CountDownTimer
                        startDate={campaignStartDate}
                        endDate={campaignEndDate}
                        model={model}
                        isAuthorMode={isAuthorMode}
                        onCountdownComplete={handleCountdownComplete}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </StyledOffersMBEHero>
      )}
    </EditableComponent>
  );
};
